import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Message, Modal } from 'semantic-ui-react'
import { X } from 'phosphor-react'
import { FlexRowBetween, TitleText } from 'components'
import { useNotification } from 'hooks/toast'
import { AuthService, MaintenanceService } from 'utility'
import { AssetManagerService } from 'utility/services/asset-manager'
import {
    PRIORITY_OPTIONS,
    buildCreatePayload,
    emptyCreateForm,
    existingTasksText,
    formFromFindingPrefill,
    maintenanceApiError,
} from './maintenance-utils'
import { VerificationSettings } from './VerificationSettings'
import { FindingEvidence } from './FindingEvidence'

/**
 * Create a maintenance task (EAUDITOR-407): the work checklist is an inspection template, the backend creates its
 * audit for the assignee. Opened from the task list ("Create Task" > Maintenance) and from Asset Details.
 * Props: open, onClose(created?), presetAsset ({AssetId, AssetName}) to lock the asset, templateOptions,
 * userOptions, customerOptions, onSwitchToInspection (task list: back to the inspection task form).
 */
export const MaintenanceTaskModal = ({
    open,
    onClose,
    presetAsset = null,
    templateOptions = [],
    userOptions = [],
    customerOptions = [],
    onSwitchToInspection,
    prefill = null,
}) => {
    const { successToast } = useNotification()
    const [form, setForm] = useState(() => emptyCreateForm())
    const [errors, setErrors] = useState({})
    const [serverError, setServerError] = useState('')
    const [saving, setSaving] = useState(false)
    const [types, setTypes] = useState([])
    const [assets, setAssets] = useState([])
    const [assetsLoading, setAssetsLoading] = useState(false)

    useEffect(() => {
        if (!open) return
        const me = AuthService.getUser()
        setForm({
            ...emptyCreateForm({ AssetId: presetAsset?.AssetId }),
            AssigneeUserId: me?.unique_id || me?.UserId || '',
            ...formFromFindingPrefill(prefill),
        })
        setErrors({})
        setServerError('')
        MaintenanceService.types()
            .then(result => setTypes(Array.isArray(result) ? result : []))
            .catch(() => setTypes([]))
        if (!presetAsset?.AssetId) {
            setAssetsLoading(true)
            AssetManagerService.assetList({ CompanyId: me?.CompanyId, OnlyActive: true })
                .then(result => {
                    const payload = result?.data || result
                    const list = Array.isArray(payload) ? payload : payload?.Data || payload?.Items || []
                    setAssets(list)
                })
                .catch(() => setAssets([]))
                .finally(() => setAssetsLoading(false))
        }
    }, [open, presetAsset?.AssetId, prefill])

    const assetOptions = useMemo(() => {
        if (presetAsset?.AssetId) {
            return [{ key: presetAsset.AssetId, value: presetAsset.AssetId, text: presetAsset.AssetName || `Asset ${presetAsset.AssetId}` }]
        }
        return assets.map(a => ({
            key: a.AssetId,
            value: a.AssetId,
            text: a.AssetTag ? `${a.AssetName} (${a.AssetTag})` : a.AssetName,
        }))
    }, [assets, presetAsset])

    const typeOptions = useMemo(
        () => [
            { key: 0, value: '', text: 'Not set' },
            ...types.map(t => ({ key: t.MaintenanceTypeId, value: t.MaintenanceTypeId, text: t.Name })),
        ],
        [types],
    )

    const departmentOptions = useMemo(
        () => [{ key: 0, value: '', text: 'No department' }, ...(customerOptions || [])],
        [customerOptions],
    )

    const set = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }))
    }

    // blocks a second click before React has re-rendered the disabled button
    const submittingRef = useRef(false)

    const submit = async () => {
        if (submittingRef.current) return
        const { errors: found, payload } = buildCreatePayload(form)
        if (Object.values(found).some(Boolean)) {
            setErrors(found)
            return
        }
        submittingRef.current = true
        setSaving(true)
        setServerError('')
        try {
            const created = await MaintenanceService.create(payload)
            successToast('Maintenance task created')
            onClose(created || true)
        } catch (error) {
            setServerError(maintenanceApiError(error))
        } finally {
            submittingRef.current = false
            setSaving(false)
        }
    }

    return (
        <Modal open={open} onClose={() => !saving && onClose()} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Create Maintenance Task</TitleText>
                    <X size={20} className="pointer" onClick={() => !saving && onClose()} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {onSwitchToInspection && (
                    <div className="mb-3">
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Task type</div>
                        <Button.Group size="small">
                            <Button type="button" basic onClick={() => !saving && onSwitchToInspection()}>
                                Inspection
                            </Button>
                            <Button type="button" primary>
                                Maintenance
                            </Button>
                        </Button.Group>
                    </div>
                )}
                {prefill && (
                    <Message size="small" info>
                        <div style={{ fontWeight: 600 }}>
                            From {prefill.TicketId ? `ticket #${prefill.TicketId} - ` : ''}
                            {prefill.AuditName || prefill.TemplateName || 'inspection'}
                        </div>
                        {prefill.QuestionText && (
                            <div>
                                {prefill.QuestionText}
                                {prefill.AnswerText ? ` - ${prefill.AnswerText}` : ''}
                            </div>
                        )}
                        <FindingEvidence notes={prefill.Notes} photos={prefill.Photos} />
                        {!prefill.AssetId && <div style={{ marginTop: 6 }}>The inspection has no asset - choose the asset below.</div>}
                        {existingTasksText(prefill) && <div style={{ marginTop: 6, color: '#92400e' }}>{existingTasksText(prefill)}</div>}
                    </Message>
                )}
                <Form noValidate>
                    <Form.Dropdown
                        label="Asset"
                        required
                        selection
                        search
                        loading={assetsLoading}
                        disabled={!!presetAsset?.AssetId}
                        placeholder="Select the asset"
                        options={assetOptions}
                        value={form.AssetId}
                        onChange={(e, { value }) => set('AssetId', value)}
                        error={errors.AssetId ? { content: errors.AssetId } : undefined}
                    />
                    <Form.Dropdown
                        label="Work checklist (inspection template)"
                        required
                        selection
                        search
                        placeholder="Select the checklist the technician fills in"
                        options={templateOptions}
                        value={form.WorkTemplateId}
                        onChange={(e, { value }) => set('WorkTemplateId', value)}
                        error={errors.WorkTemplateId ? { content: errors.WorkTemplateId } : undefined}
                    />
                    <Form.Group widths="equal">
                        <Form.Dropdown
                            label="Assigned to"
                            selection
                            search
                            options={userOptions}
                            value={form.AssigneeUserId}
                            onChange={(e, { value }) => set('AssigneeUserId', value)}
                        />
                        <Form.Dropdown
                            label="Department / customer"
                            selection
                            search
                            placeholder="No department"
                            options={departmentOptions}
                            value={form.CustomerId}
                            onChange={(e, { value }) => set('CustomerId', value)}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Dropdown
                            label="Maintenance type"
                            selection
                            placeholder="Not set"
                            options={typeOptions}
                            value={form.MaintenanceTypeId}
                            onChange={(e, { value }) => set('MaintenanceTypeId', value)}
                        />
                        <Form.Dropdown
                            label="Priority"
                            selection
                            options={PRIORITY_OPTIONS}
                            value={form.PriorityId}
                            onChange={(e, { value }) => set('PriorityId', value)}
                        />
                        <Form.Input
                            label="Due date"
                            required
                            type="date"
                            value={form.TaskDue}
                            onChange={(e, { value }) => set('TaskDue', value)}
                            error={errors.TaskDue ? { content: errors.TaskDue } : undefined}
                        />
                    </Form.Group>
                    <Form.Input
                        label="Title"
                        placeholder="Default: <maintenance type>: <asset>"
                        maxLength={200}
                        value={form.Title}
                        onChange={(e, { value }) => set('Title', value)}
                        error={errors.Title ? { content: errors.Title } : undefined}
                    />
                    <Form.TextArea
                        label="Instructions"
                        rows={3}
                        maxLength={2000}
                        value={form.Instructions}
                        onChange={(e, { value }) => set('Instructions', value)}
                        error={errors.Instructions ? { content: errors.Instructions } : undefined}
                    />
                    <Form.Checkbox
                        label="Verification required after the work"
                        checked={!!form.VerificationRequired}
                        onChange={(e, { checked }) => set('VerificationRequired', checked)}
                    />
                    {form.VerificationRequired && (
                        <VerificationSettings
                            form={form}
                            set={set}
                            templateOptions={templateOptions}
                            userOptions={userOptions}
                            errors={errors}
                        />
                    )}
                </Form>
                {serverError && <Message negative content={serverError} />}
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={() => onClose()} disabled={saving}>
                    Cancel
                </Button>
                <Button type="button" primary onClick={submit} loading={saving} disabled={saving}>
                    Create
                </Button>
            </Modal.Actions>
        </Modal>
    )
}
